<template>
  <!-- Default Pagination Type -->
  <div v-if="props.type === 'default'" class="flex items-center justify-end w-full pt-2">
    <!-- Mobile page info (only visible on mobile) -->
    <div v-if="isMobile && computedPageCount > 3" class="text-xs text-gray-500 mr-1 sm:hidden">
      {{ currentPage }}/{{ computedPageCount }}
    </div>

    <div class="flex rounded-md shadow-sm space-x-0.5 sm:space-x-0">
      <!-- First page button (always visible) -->
      <button :disabled="currentPage === 1" @click="handlePageChange(1)" :class="[buttonClasses, 'rounded-l-md sm:rounded-l-md sm:rounded-r-none sm:border-r-0 px-1 sm:px-2']" aria-label="İlk sayfa">
        «
      </button>

      <!-- Adjusted Previous page button -->
      <button :disabled="currentPage === 1" @click="handlePageChange(currentPage - 1)" :class="[buttonClasses, 'sm:rounded-none sm:border-r-0 px-1.5 sm:px-2']" aria-label="Önceki sayfa">
        ‹
      </button>

      <!-- Page numbers (conditionally visible) -->
      <button v-for="(page, index) in visiblePages" :key="index" :class="[
buttonClasses, 
{ 'selected': page === currentPage },
{ 'hidden xs:flex': !isMobile && typeof page === 'number' && page !== currentPage },
  'sm:rounded-none sm:border-r-0'
]" @click="
  page === '...' ? openDropdown($event, index) : handlePageChange(page)
" :ref="page === '...' ? `ellipsis-${index}` : null">
        {{ page }}
      </button>

      <!-- Adjusted Next page button -->
      <button :disabled="currentPage === computedPageCount" @click="handlePageChange(currentPage + 1)" :class="[buttonClasses, 'sm:rounded-none sm:border-r-0 px-1.5 sm:px-2']" aria-label="Sonraki sayfa">
        ›
      </button>

      <!-- Last page button (always visible) -->
      <button :disabled="currentPage === computedPageCount" @click="handlePageChange(computedPageCount)" :class="[buttonClasses, 'rounded-r-md sm:rounded-r-md sm:rounded-l-none px-1 sm:px-2']" aria-label="Son sayfa">
        »
      </button>
    </div>

    <!-- Page jump dropdown -->
    <Teleport to="body">
      <div v-if="showDropdown" ref="dropdown" v-click-outside="closeDropdown" class="bg-white border border-gray-300 rounded-md shadow-lg p-2 z-50" :style="{
position: 'absolute',
top: `${dropdownPosition.top}px`,
  left: `${dropdownPosition.left}px`,
}">
        <input v-model.number="jumpToPage" @keyup.enter="handleJumpToPage" :class="inputClasses" type="number" min="1" :max="computedPageCount" :placeholder="`1-${computedPageCount}`"/>
        <button @click="handleJumpToPage" :class="dropdownButtonClasses">
          {{ t('general.go') }}
        </button>
      </div>
    </Teleport>
  </div>

  <!-- Simple Pagination Type -->
  <div v-else class="flex flex-col sm:flex-row items-center justify-between w-full py-2 px-4 bg-white border-t border-gray-200 gap-2">
    <!-- Sonuç Sayısı (Sol Tarafta) -->
    <div class="text-sm text-gray-500 order-2 sm:order-1 text-center sm:text-left">
      {{ currentPage }}-{{ computedPageCount }} / {{ props.totalCount || 0 }} sonuç
    </div>

    <!-- Pagination Controls (Sağ Tarafta) -->
    <div class="flex items-center justify-center flex-wrap gap-1 order-1 sm:order-2 w-full sm:w-auto">
      <!-- Navigation Controls -->
      <div class="flex items-center shadow-sm rounded-md overflow-hidden border border-gray-200">
        <!-- First Page Button -->
        <button @click="handlePageChange(1)" :disabled="currentPage === 1" 
          class="w-8 h-8 flex items-center justify-center text-sm border-r border-gray-200" 
          :class="simpleBtnStyleClass(currentPage === 1)" 
          aria-label="İlk sayfa">
          <span class="material-symbols-outlined !text-[18px]">first_page</span>
        </button>

        <!-- Previous Page Button -->
        <button @click="handlePageChange(currentPage - 1)" :disabled="currentPage === 1" 
          class="w-8 h-8 flex items-center justify-center text-sm border-r border-gray-200" 
          :class="simpleBtnStyleClass(currentPage === 1)" 
          aria-label="Önceki sayfa">
          <span class="material-symbols-outlined !text-[18px]">navigate_before</span>
        </button>

        <!-- Interactive Page Input/Display -->
        <div @click="openPageSelector" class="min-w-[60px] px-3 flex items-center justify-center border-r border-gray-200 h-8 cursor-pointer">
          <span class="text-sm font-medium text-gray-700 hover:text-primary whitespace-nowrap">
            {{ currentPage }} / {{ computedPageCount }}
          </span>
        </div>

        <!-- Next Page Button -->
        <button @click="handlePageChange(currentPage + 1)" :disabled="currentPage === computedPageCount" 
          class="w-8 h-8 flex items-center justify-center text-sm border-r border-gray-200" 
          :class="simpleBtnStyleClass(currentPage === computedPageCount)" 
          aria-label="Sonraki sayfa">
          <span class="material-symbols-outlined !text-[18px]">navigate_next</span>
        </button>

        <!-- Last Page Button -->
        <button @click="handlePageChange(computedPageCount)" :disabled="currentPage === computedPageCount" 
          class="w-8 h-8 flex items-center justify-center text-sm" 
          :class="simpleBtnStyleClass(currentPage === computedPageCount)" 
          aria-label="Son sayfa">
          <span class="material-symbols-outlined !text-[18px]">last_page</span>
        </button>
      </div>

      <!-- Page Selector Input (Hidden by default) -->
      <div v-if="showDropdown" class="absolute bottom-16 bg-white shadow-md border border-gray-200 rounded-md p-2 z-50">
        <div class="flex items-center">
          <input ref="pageInputRef" v-model.number="jumpToPage" @keyup.enter="handleJumpToPage; closeDropdown()" type="number" min="1" :max="computedPageCount" class="w-16 text-center border border-gray-300 rounded-md p-1 text-sm"/>
          <button @click="handleJumpToPage; closeDropdown()" class="ml-2 px-2 py-1 bg-primary text-white text-xs rounded-md">
            Git
          </button>
        </div>
      </div>

      <!-- Sayfa Boyutu Seçici -->
      <div class="flex items-center ml-2 bg-white rounded-md border border-gray-200 shadow-sm overflow-hidden h-8">
        <span class="text-xs text-gray-500 whitespace-nowrap px-2 border-r border-gray-200 h-full flex items-center">Sayfa Başına:</span>
        <select :value="props.itemsPerPage" @change="$emit('page-items-changed', parseInt($event.target.value))" class="text-xs h-full px-2 appearance-none bg-white focus:outline-none">
          <option v-for="size in pageSizeOptions" :key="size" :value="size">
            {{ size }}
          </option>
        </select>
        <div class="pointer-events-none px-1 text-gray-400 flex items-center">
          <span class="material-symbols-outlined !text-[16px]">expand_more</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { computePosition, flip, shift, offset } from '@floating-ui/dom'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  totalCount: Number,
  itemsPerPage: {
    type: Number,
    default: 10,
  },
  currentPage: {
    type: Number,
    default: 1,
  },
  pageCount: {
    type: Number,
    default: null,
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['sm', 'md', 'lg'].includes(value),
  },
  maxVisiblePages: {
    type: Number,
    default: 7,
  },
  type: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'simple'].includes(value),
  },
})

const { t } = useI18n()

const emit = defineEmits(['page-changed', 'page-items-changed'])

const showDropdown = ref(false)
const dropdownPosition = ref({ top: 0, left: 0 })
const jumpToPage = ref('')
const dropdown = ref(null)
const activeEllipsis = ref(null)
const windowWidth = ref(window.innerWidth)

// Detect if we're on mobile
const isMobile = computed(() => windowWidth.value < 640)

// Handle window resize
const updateWindowWidth = () => {
  windowWidth.value = window.innerWidth
}

// Adjust number of visible pages based on screen size
const adjustedMaxVisiblePages = computed(() => {
  if (windowWidth.value < 640) return 3
  if (windowWidth.value < 768) return 5
  return props.maxVisiblePages
})

const buttonClasses = computed(() => {
  const baseClasses = 'paginate-button flex items-center justify-center bg-white border border-gray-300 !text-secondary cursor-pointer transition-all duration-200 ease-in-out'
  const sizeClasses = {
    sm: 'h-7 px-1 min-w-[26px] text-xs',
    md: 'h-8 px-1.5 min-w-[28px] text-sm',
    lg: 'h-9 px-2 min-w-[32px] text-base',
  }
  return `${baseClasses} ${sizeClasses[props.size]}`
})

const inputClasses = computed(() => {
  const baseClasses = 'px-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary'
  const sizeClasses = {
    sm: 'w-12 py-0.5 text-xs',
    md: 'w-16 py-1 text-sm',
    lg: 'w-20 py-1.5 text-base',
  }
  return `${baseClasses} ${sizeClasses[props.size]}`
})

const dropdownButtonClasses = computed(() => {
  const baseClasses = 'ml-2 bg-primary text-white rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary'
  const sizeClasses = {
    sm: 'px-1.5 py-0.5 text-xs',
    md: 'px-2 py-1 text-sm',
    lg: 'px-3 py-1.5 text-base',
  }
  return `${baseClasses} ${sizeClasses[props.size]}`
})

// Helper for simple pagination button styles
const simpleBtnStyleClass = (isDisabled) => {
  return isDisabled
      ? 'text-gray-400 cursor-not-allowed bg-gray-50'
      : 'text-gray-700 hover:bg-gray-100 bg-white'
}

// Page size options for dropdown
const pageSizeOptions = [10, 25, 50, 100]

const computedPageCount = computed(() => {
  return props.pageCount !== null
      ? props.pageCount
      : Math.ceil(props.totalCount / props.itemsPerPage)
})

const visiblePages = computed(() => {
  const range = []
  const maxPages = adjustedMaxVisiblePages.value

  if (computedPageCount.value <= maxPages) {
    for (let i = 1; i <= computedPageCount.value; i++) {
      range.push(i)
    }
  } else {
    // Handle simplified pagination for mobile
    if (isMobile.value) {
      // On mobile, show minimal pagination to save space
      if (computedPageCount.value <= 3) {
        // If we have 3 or fewer pages, show all
        for (let i = 1; i <= computedPageCount.value; i++) {
          range.push(i)
        }
      } else if (props.currentPage === 1) {
        // If we're on page 1, show pages 1-2
        range.push(1, 2)
      } else if (props.currentPage === computedPageCount.value) {
        // If we're on the last page, show last two pages
        range.push(computedPageCount.value - 1, computedPageCount.value)
      } else {
        // Otherwise, show current page
        range.push(props.currentPage)
      }
      return range
    }

    // Handle full pagination for larger screens
    range.push(1)
    if (props.currentPage > 3) range.push('...')

    let start = Math.max(2, props.currentPage - 1)
    let end = Math.min(computedPageCount.value - 1, props.currentPage + 1)

    if (props.currentPage < 3) end = 4
    if (props.currentPage > computedPageCount.value - 2)
      start = computedPageCount.value - 3

    for (let i = start; i <= end; i++) {
      range.push(i)
    }

    if (props.currentPage < computedPageCount.value - 2) range.push('...')
    range.push(computedPageCount.value)
  }

  return range
})

const handlePageChange = (page) => {
  if (
      page !== props.currentPage &&
      page !== '...' &&
      page >= 1 &&
      page <= computedPageCount.value
  ) {
    emit('page-changed', page)
    showDropdown.value = false
  }
}

const openDropdown = (event, index) => {
  activeEllipsis.value = event.target
  showDropdown.value = true
  jumpToPage.value = ''
  updateDropdownPosition()
}

const updateDropdownPosition = () => {
  if (activeEllipsis.value && dropdown.value) {
    computePosition(activeEllipsis.value, dropdown.value, {
      placement: 'bottom',
      middleware: [offset(5), flip(), shift({ padding: 5 })],
    }).then(({ x, y }) => {
      dropdownPosition.value = { top: y, left: x }
    })
  }
}

const handleJumpToPage = () => {
  const page = parseInt(jumpToPage.value)
  if (page >= 1 && page <= computedPageCount.value) {
    handlePageChange(page)
    showDropdown.value = false
  }
}

const closeDropdown = () => {
  if (!activeEllipsis.value?.contains(event?.target)) {
    showDropdown.value = false
  }
}

onMounted(() => {
  window.addEventListener('resize', updateWindowWidth)
  window.addEventListener('resize', updateDropdownPosition)
  window.addEventListener('scroll', updateDropdownPosition)
})

onUnmounted(() => {
  window.removeEventListener('resize', updateWindowWidth)
  window.removeEventListener('resize', updateDropdownPosition)
  window.removeEventListener('scroll', updateDropdownPosition)
})
</script>

<style scoped>
.paginate-button:not(:first-child) {
  @apply sm:border-l-0;
}

.paginate-button.selected {
  @apply !bg-primary !text-white border-primary shadow-md z-10;
}

.paginate-button:disabled {
  @apply cursor-not-allowed bg-gray-200 border-gray-200 !text-gray-400;
}

.paginate-button:hover:not(:disabled) {
  @apply bg-gray-100;
}

/* Make the buttons more touch-friendly on mobile */
@media (max-width: 640px) {
  .paginate-button {
    @apply min-w-[28px] h-8 text-xs border-gray-300 px-0.5;
  }

  /* Ensure tap targets are large enough but not too big */
  button {
    @apply touch-manipulation;
  }

  /* Add less space between buttons on mobile but keep them separate */
  .paginate-button + .paginate-button {
    @apply ml-0.5;
  }

  /* Enhance visibility of selected page on mobile */
  .paginate-button.selected {
    @apply !bg-primary !text-white border-primary shadow-sm;
  }
}

/* Extra small screens utility */
.xs\:flex {
  @media (min-width: 480px) {
    display: flex;
  }
}
</style>